import { useReducer } from 'react';

import { getNextIndex, getPreviousIndex } from '~shared/utils/get-index';

import {
  Action,
  HAS_SUGGESTIONS_CHANGED,
  INPUT_ARROW_DOWN,
  INPUT_ARROW_UP,
  INPUT_BLUR,
  INPUT_CHANGE,
  INPUT_ENTER,
  INPUT_ESCAPE,
  INPUT_FOCUS,
  SUGGESTION_MOUSE_CLICK,
  SUGGESTION_MOUSE_ENTER,
} from './actions';
import { AutosuggestValue } from './types';

interface AutosuggestState<Value> {
  highlightedIndex: number | null;
  inputChangedSinceFocus: boolean;
  isFocused: boolean;
  previewValue: AutosuggestValue<Value> | null;
  showSuggestionsIfAvailable: boolean;
}

export function useAutosuggestReducer<Value>({
  automaticSelection,
  hasItems,
  hasSuggestions,
  hideSuggestionsOnSelection,
  suggestionCount,
  suggestions,
  value,
}) {
  const reducer = (state: AutosuggestState<Value>, action: Action) => {
    switch (action.type) {
      case INPUT_ARROW_DOWN: {
        if (hasSuggestions) {
          const newIndex = getNextIndex(
            state.highlightedIndex,
            suggestionCount
          );

          return {
            ...state,
            showSuggestionsIfAvailable: true,
            previewValue: suggestions[newIndex],
            highlightedIndex: newIndex,
          };
        }

        return state;
      }

      case INPUT_ARROW_UP: {
        if (hasSuggestions) {
          const newIndex = getPreviousIndex(
            state.highlightedIndex,
            suggestionCount
          );

          return {
            ...state,
            showSuggestionsIfAvailable: true,
            previewValue: suggestions[newIndex],
            highlightedIndex: newIndex,
          };
        }

        return state;
      }

      case INPUT_CHANGE: {
        return {
          ...state,
          showSuggestionsIfAvailable: true,
          inputChangedSinceFocus: true,
          previewValue: null,
          highlightedIndex:
            hasSuggestions && automaticSelection && value.text.length > 0
              ? 0
              : null,
        };
      }

      case INPUT_FOCUS: {
        return {
          ...state,
          showSuggestionsIfAvailable: true,
          inputChangedSinceFocus: false,
          isFocused: true,
        };
      }

      case INPUT_BLUR: {
        return {
          ...state,
          showSuggestionsIfAvailable: false,
          previewValue: null,
          highlightedIndex: null,
          isFocused: false,
        };
      }

      case INPUT_ESCAPE: {
        if (value.text) {
          return {
            ...state,
            showSuggestionsIfAvailable: false,
            previewValue: null,
            highlightedIndex: null,
          };
        } else if (hasItems) {
          return {
            ...state,
            showSuggestionsIfAvailable: !state.showSuggestionsIfAvailable,
            previewValue: null,
          };
        }

        return state;
      }

      case INPUT_ENTER:
      case SUGGESTION_MOUSE_CLICK: {
        return {
          ...state,
          showSuggestionsIfAvailable: !hideSuggestionsOnSelection,
          previewValue: null,
          highlightedIndex: null,
        };
      }

      case SUGGESTION_MOUSE_ENTER: {
        return {
          ...state,
          highlightedIndex: action.value,
        };
      }

      case HAS_SUGGESTIONS_CHANGED: {
        return automaticSelection
          ? {
              ...state,
              highlightedIndex:
                hasSuggestions && value.text.length > 0 ? 0 : null,
            }
          : state;
      }

      default: {
        // eslint-disable-next-line no-console
        console.error(new Error(`Invalid Autosuggest action: ${action}`));
        return state;
      }
    }
  };

  return useReducer(reducer, {
    highlightedIndex: null,
    inputChangedSinceFocus: false,
    isFocused: false,
    previewValue: null,
    showSuggestionsIfAvailable: false,
  });
}
